import { SocialConnection } from '../../../api';
import { ConfigurableField } from '../../../api/configurations/social/types';
import { validateUrlLink } from '../../../utils';
import { FieldNames, filterValues, FormValues } from './types';

export const clientIdMaxLength = 255;

export const socialSettingsValidator =
  (socialConnection: SocialConnection) =>
  (values: FormValues): Record<string, string | string[]> => {
    const error: Record<string, string | string[]> = {};

    const hiddenFields: string[] =
      socialConnection.provider === 'xsolla-id'
        ? ['client_id', 'client_secret', 'custom_return_url']
        : [];

    if (values?.custom_return_url && !validateUrlLink(values.custom_return_url as string)) {
      error.custom_return_url = 'app.validation.url-invalid';
    }

    if (values?.url_authorize && !validateUrlLink(values.url_authorize as string)) {
      error.url_authorize = 'app.validation.url-invalid';
    }

    if (values?.url_access_token && !validateUrlLink(values.url_access_token as string)) {
      error.url_access_token = 'app.validation.url-invalid';
    }

    if (values?.audience && !validateUrlLink(values.audience as string)) {
      error.audience = 'app.validation.url-invalid';
    }

    if (values?.client_id && values.client_id.length > clientIdMaxLength) {
      error.client_id = 'app.validation.characters';
    }

    if (
      Object.entries(values).some(([key, formValue]) => !filterValues.includes(key) && formValue)
    ) {
      Object.entries(values).forEach(([key, formValue]) => {
        if (
          key !== FieldNames.CUSTOM_RETURN_URL &&
          key !== FieldNames.REFRESH_ALWAYS &&
          !hiddenFields.includes(key) &&
          !formValue
        ) {
          error[key] = 'app.validation.required';
        }
      });
    }

    socialConnection.configurable_fields.forEach((field) => {
      const err = getConfigFieldError(values, field);

      if (err) {
        if (Array.isArray(err)) {
          error[field.name] = [];

          err.forEach((errMess, index) => {
            (error[field.name] as string[]).push(errMess);
          });
        } else {
          error[field.name] = err;
        }
      }
    });

    return error;
  };

const getConfigFieldError = (
  values: FormValues,
  field: ConfigurableField
): string | string[] | null => {
  if (!field.required) {
    return null;
  }

  if (Array.isArray(values[field.name])) {
    const fieldValues = values[field.name] as string[];

    if (fieldValues.length === 0) {
      return 'app.validation.required';
    }

    const errors = fieldValues.map((value, index, array) => {
      if (!value) {
        return 'app.validation.required';
      }

      if (!validateUrlLink(value)) {
        return 'app.validation.url-invalid';
      }

      const copyArr = [...array];
      copyArr.splice(index, 1);

      if (copyArr.includes(value)) {
        return 'app.validation.url-duplicate';
      }

      return '';
    });

    return errors.some((err) => err) ? errors : null;
  }

  if (!values[field.name]) {
    return 'app.validation.required';
  }

  return null;
};
